import { cookies } from '@zing/shared/cookies/behavior';
import { decide, allowRedirect, clearLoopGuard, normalizePath, type Verdict } from './decisionCore';

export interface SmartNavConfig {
  // Base URL of the official backend (REACT_APP_BASE_API_URL) — the feature-flag endpoint
  // is just another route on it. The full flags URL is derived from this + tenantDomain,
  // so there is no separate REACT_APP_UNLEASH_PROXY_URL.
  apiBaseUrl: string;
  tenantDomain: string;
  pollIntervalMs: number;
  registeredFullPaths: ReadonlySet<string>;
  // Unmigrated routes are sent straight to the legacy host in every environment
  // (local: http://localhost:4000, server: the paired legacy subdomain).
  legacyUrl: string;
  // Static escape-hatch prefixes that always fall back to legacy, regardless of
  // Unleash. Mirrors the (now-removed) Vite middleware's VITE_LEGACY_PATH_PREFIXES.
  legacyPathPrefixes: string[];
}

const CACHE_PREFIX = 'zing:smartnav:toggles:';
const FAIL_TOWARD_LEGACY_MS = 1500;

let _config: SmartNavConfig | null = null;
// True only once a real toggle set is available — a *successful* authenticated fetch or a
// warm cache hydration. All redirect decisions gate on this: a "poll attempted" flag is
// not enough, because pre-login the first poll is skipped for lack of a token, which would
// leave an empty map that a just-logged-in session decides against and fails toward legacy.
let _togglesLoaded = false;
let _toggles: Record<string, boolean> = {};
let _registry: string[] = [];
// Listeners notified whenever the toggle map changes (cache hydration or a refresh), so
// React consumers (e.g. the side menu) re-render when flags arrive or flip.
const _togglesListeners = new Set<() => void>();

function notifyTogglesChanged(): void {
  _togglesListeners.forEach((listener) => listener());
}

export function subscribeToggles(listener: () => void): () => void {
  _togglesListeners.add(listener);
  return () => {
    _togglesListeners.delete(listener);
  };
}

/**
 * Per-route flag only (`<DOMAIN>.FALLBACK-ACTIVE.<NORMALIZED_PATH>`), the same key decide()
 * uses. Fail-closed: false while toggles are not loaded or the flag is absent.
 */
export function isRouteFlagEnabled(path: string): boolean {
  if (!_config) return false;
  const domain = (_config.tenantDomain || 'localhost').toUpperCase();
  return _toggles[`${domain}.FALLBACK-ACTIVE.${normalizePath(path)}`] === true;
}
let _pollTimer: ReturnType<typeof setInterval> | null = null;
// Dedupe concurrent on-demand refreshes so multiple callers share one /flags request.
let _inflightRefresh: Promise<void> | null = null;

// The feature-flag endpoint requires a user JWT. Pre-login there is no token, so we
// skip the poll entirely and never redirect — the shell renders in place. The token is
// shared with the legacy via the `_cred` cookie (or sessionStorage on the originating app).
export function getAuthToken(): string | null {
  try {
    const cookieToken = cookies.get('_cred')?.data?.token as string | undefined;
    const sessionToken = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('jwt') : null;
    return cookieToken || sessionToken || null;
  } catch {
    return null;
  }
}

export function hasAuthToken(): boolean {
  return getAuthToken() !== null;
}

function cacheKey(): string {
  return CACHE_PREFIX + (_config?.tenantDomain ?? 'localhost');
}

// Build the feature-flag endpoint on the official backend, mirroring how every other API
// route is composed: {apiBaseUrl}/v2/feature-flags/domain/{tenantDomain}/flags.
function flagsEndpoint(): string {
  const base = (_config?.apiBaseUrl ?? '').replace(/\/+$/, '');
  if (!base) return '';
  const tenant = _config?.tenantDomain ?? 'localhost';
  return `${base}/v2/feature-flags/domain/${encodeURIComponent(tenant)}/flags`;
}

function loadCachedToggles(): Record<string, boolean> {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(cacheKey()) : null;
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

function persistToggles(toggles: Record<string, boolean>): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(cacheKey(), JSON.stringify(toggles));
  } catch {
    /* ignore quota/availability errors */
  }
}

export function initSmartNav(cfg: SmartNavConfig): void {
  _config = cfg;
  _registry = [...cfg.registeredFullPaths];

  // Stale-while-revalidate: hydrate from the last cached toggles so decisions are
  // instant on a cold page load, then refresh against the backend in the background.
  const cached = loadCachedToggles();
  if (Object.keys(cached).length > 0) {
    _toggles = cached;
    _togglesLoaded = true;
    notifyTogglesChanged();
  }

  // Kick the first poll (skipped pre-login for lack of a token). Route it through the
  // dedupe helper so a concurrent ensureDecision coalesces onto this same request.
  void refreshFlagsOnce();

  if (_pollTimer !== null) clearInterval(_pollTimer);
  _pollTimer = setInterval(() => { _refreshFlags(); }, cfg.pollIntervalMs);
}

export async function _refreshFlags(): Promise<void> {
  // No token (pre-login) → skip the poll, keep the stale/empty cache, never redirect.
  const url = flagsEndpoint();
  const token = getAuthToken();
  if (!url || !token) return;
  try {
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return;
    const json = await res.json();
    const next: Record<string, boolean> = {};
    for (const t of (json.toggles ?? [])) next[t.name] = t.enabled === true;
    _toggles = next;
    _togglesLoaded = true;
    persistToggles(next);
    notifyTogglesChanged();
  } catch {
    // keep stale cache on error
  }
}

// Deduped on-demand refresh: coalesces concurrent callers onto one in-flight /flags fetch.
// Used by ensureDecision to load toggles the moment a token appears (login) without firing
// a request per navigation/guard call.
function refreshFlagsOnce(): Promise<void> {
  if (_inflightRefresh) return _inflightRefresh;
  _inflightRefresh = _refreshFlags().finally(() => { _inflightRefresh = null; });
  return _inflightRefresh;
}

export function isSmartNavReady(): boolean {
  return _togglesLoaded;
}

/**
 * Static escape-hatch match, decided WITHOUT flags or a token. These paths are legacy-owned
 * unconditionally — including in the not-logged area, where there is no JWT to poll the flag
 * endpoint with, so useServerFallbackGuard bails before decide() ever consults the prefixes.
 * Public legacy-only pages (e.g. the meeting-room reservation form) live here until they are
 * ported to the shell. Mirrors the escape-hatch branch at the top of decide().
 */
export function isLegacyEscapeHatch(pathname: string): boolean {
  for (const prefix of _config?.legacyPathPrefixes ?? []) {
    if (pathname === prefix || pathname.startsWith(prefix + '/')) return true;
  }
  return false;
}

/** Verdict for a path using the current (possibly cached) toggles + registry. */
export function decideForPath(pathname: string): Verdict {
  if (!_config) return 'legacy';
  return decide(pathname, {
    registry: _registry,
    toggles: _toggles,
    tenantDomain: _config.tenantDomain,
    escapeHatchPrefixes: _config.legacyPathPrefixes,
    isAuthenticated: hasAuthToken(),
  });
}

export function shouldUseClientNav(pathname: string): boolean {
  // Synchronous decision for click-time interceptors (sidebar/menu) that only run once the
  // user is on an authenticated page — toggles are already loaded by then. Without a real
  // toggle set, fail toward legacy. For the just-logged-in navigation, use the async
  // ensureDecision instead, which loads the toggles first.
  if (!_togglesLoaded || !_config) return false;
  return decideForPath(pathname) === 'shell';
}

/**
 * Block until a decision can be made: resolves immediately when toggles are already
 * loaded (warm cache or a completed fetch). Otherwise — the common case right after login,
 * when a token has just appeared but the 15s poll hasn't fired — triggers a fresh fetch and
 * awaits it, bounded by a fail-toward-legacy timeout so a slow/down backend never hangs.
 */
export async function ensureDecision(pathname: string): Promise<Verdict> {
  if (_togglesLoaded) return decideForPath(pathname);
  await Promise.race([
    refreshFlagsOnce(),
    new Promise<void>((resolve) => setTimeout(resolve, FAIL_TOWARD_LEGACY_MS)),
  ]);
  // If still not loaded (timeout, no token, no proxy), decideForPath returns 'legacy'
  // because the toggle map is empty — i.e. fail toward legacy.
  return decideForPath(pathname);
}

// Single authority, every environment: always send unmigrated routes to the legacy host.
// (Local dev no longer bounces through the shell — there is no middleware to re-decide.)
export function buildFallbackUrl(path: string, search = '', hash = ''): string {
  const base = _config?.legacyUrl ?? '';
  const normalized = path.startsWith('/') ? path : '/' + path;
  return base + normalized + search + hash;
}

/**
 * Loop-guarded hard redirect to the fallback. Returns true if it redirected, false if
 * the loop guard tripped (caller should render in place). Use this instead of calling
 * window.location.replace(buildFallbackUrl(...)) directly.
 */
export function redirectToFallback(path: string, search = '', hash = ''): boolean {
  if (!allowRedirect(path)) return false;
  window.location.replace(buildFallbackUrl(path, search, hash));
  return true;
}

/** Call once a page renders without redirecting, to reset the loop counter. */
export function markSettled(): void {
  clearLoopGuard();
}
